import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public paths where 401s should NOT redirect (login attempts, public landing/checkout)
const PUBLIC_PATHS = ['/', '/login', '/sobre', '/termos', '/privacidade', '/payment-success', '/payment-cancelled', '/change-password'];

const SubscriptionGuard = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // 403 inactive subscription → modal de reativação
        if (
          error.response?.status === 403 &&
          error.response?.data?.detail === 'subscription_inactive'
        ) {
          setShowPopup(true);
          return Promise.reject(error);
        }

        // 401 inside protected route (single source of truth for logout).
        // Skip if the call is the login attempt itself OR if user is on a public path.
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isLoginAttempt = url.includes('/api/auth/login') || url.includes('/api/auth/forgot-password');
          const isPublic = PUBLIC_PATHS.some(p => window.location.pathname === p);
          if (!isLoginAttempt && !isPublic) {
            // Defer redirect to next tick so the rejection propagates cleanly to callers
            setTimeout(() => {
              // Hard reload-style navigation ensures cookies/state are flushed
              window.location.assign('/login');
            }, 50);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const handleGoToPayment = () => {
    setShowPopup(false);
    window.location.href = '/#planos';
  };

  return (
    <Dialog open={showPopup} onOpenChange={setShowPopup}>
      <DialogContent className="sm:max-w-md" data-testid="subscription-inactive-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Plano Inativo
          </DialogTitle>
          <DialogDescription className="text-left space-y-3 pt-2">
            <p>Esta funcionalidade é exclusiva para assinantes com plano ativo.</p>
            <p>Reative sua assinatura para continuar usando todas as funcionalidades da Aretê, incluindo o bot WhatsApp.</p>
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={() => setShowPopup(false)} className="flex-1">
            Fechar
          </Button>
          <Button onClick={handleGoToPayment} className="flex-1" data-testid="reactivate-plan-btn">
            Reativar Plano
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionGuard;
